import { createSelector, createSlice, SliceCaseReducers } from '@reduxjs/toolkit';
import { Alternative } from 'app/components/People/PeopleSteps/PeopleStepsTwo/PeopleStepsTwoAlternativesList/usePeopleAlternatives';

interface PeopleStepsState {
  current_tabs: string[];
  filters: {
    locations: {
      uuid: string;
      name: string;
    }[];
    companies: {
      uuid: string;
      name: string;
    }[];
    levels: {
      uuid: string;
      name: string;
    }[];
  };
  alternatives: Alternative[];
  all_alternatives: Alternative[];
  showing_loading_screen: boolean;
}

export const slice = createSlice<PeopleStepsState, SliceCaseReducers<PeopleStepsState>>({
  name: 'people-steps',
  initialState: {
    current_tabs: ['most_relevant'],
    alternatives: [],
    all_alternatives: [],
    filters: {
      locations: [],
      companies: [],
      levels: [],
    },
    showing_loading_screen: false,
  },
  reducers: {
    setCurrentTabs: (state, action) => {
      state.current_tabs = action.payload;
      state.filters = {
        locations: [],
        companies: [],
        levels: [],
      };
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setFilter: (state, action) => {
      state.filters[action.payload.type] = action.payload.filters;
    },
    setAlternatives: (state, action) => {
      state.alternatives = action.payload;
    },
    setAllAlternatives: (state, action) => {
      state.all_alternatives = action.payload;
    },
    setShowingLoadingScreen: (state, action) => {
      state.showing_loading_screen = action.payload;
    },
  },
});

export const {
  setCurrentTabs,
  setFilters,
  setFilter,
  setAlternatives,
  setAllAlternatives,
  setShowingLoadingScreen,
} = slice.actions;

export const currentTabsSelector = (state): string[] => state.peopleSteps.current_tabs;
export const allAlternativesSelector = (state): Alternative[] => state.peopleSteps.all_alternatives;

export const filtersSelector = (
  state
): {
  locations: {
    uuid: string;
    name: string;
  }[];
  companies: {
    uuid: string;
    name: string;
  }[];
  levels: {
    uuid: string;
    name: string;
  }[];
} => state.peopleSteps.filters;

export const alternativesSelector = (state): Alternative[] => state.peopleSteps.alternatives;

export const showingLoadingScreenSelector = (state): boolean =>
  state.peopleSteps.showing_loading_screen;

export const companiesFromAlternativesSelector = createSelector(
  [alternativesSelector],
  (alternatives) => {
    const companies = alternatives.reduce((acc, alternative) => {
      return [...acc, ...alternative.originalUuidList.map((uuid) => uuid.engagement?.company)];
    }, []);

    return companies.filter(
      (company, index, self) => index === self.findIndex((c) => c?.uuid === company?.uuid)
    );
  }
);

export const locationsFromAlternativesSelector = createSelector(
  [alternativesSelector],
  (alternatives) => {
    const locations = alternatives.reduce((acc, alternative) => {
      return [...acc, ...alternative.originalUuidList.map((uuid) => uuid.location)];
    }, []);

    return locations.filter(
      (location, index, self) => index === self.findIndex((l) => l?.uuid === location?.uuid)
    );
  }
);

export const levelsFromAlternativesSelector = createSelector(
  [alternativesSelector],
  (alternatives) => {
    const levels = alternatives.reduce((acc, alternative) => {
      return [...acc, ...alternative.originalUuidList.map((uuid) => uuid.level)];
    }, []);

    return levels.filter(
      (level, index, self) => index === self.findIndex((l) => l?.uuid === level?.uuid)
    );
  }
);

export const engagementTypesFromAlternativesSelector = createSelector(
  [alternativesSelector],
  (alternatives) => {
    const engagementTypes = alternatives.reduce((acc, alternative) => {
      return [...acc, ...alternative.originalUuidList.map((uuid) => uuid.engagement.type)];
    }, []);

    return engagementTypes.filter(
      (engagementType, index, self) =>
        index === self.findIndex((e) => e.uuid === engagementType.uuid)
    );
  }
);

export const companiesFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector],
  (alternatives) => {
    const companies = alternatives.reduce((acc, alternative) => {
      return [...acc, ...alternative.uuidList.map((uuid) => uuid.engagement?.company)];
    }, []);

    return companies.filter(
      (company, index, self) => index === self.findIndex((c) => c?.uuid === company?.uuid)
    );
  }
);

export const availableCompaniesFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector, filtersSelector],
  (alternatives, filters) => {
    const { locations: selectedLocations, levels: selectedLevels } = filters;

    const uuidMap = new Map();
    alternatives.reduce((_, alternative) => {
      alternative.originalUuidList.forEach((u) => {
        if (u?.uuid && !uuidMap.has(u.uuid)) {
          uuidMap.set(u.uuid, u);
        }
      });
    }, null);

    let filteredUuids = Array.from(uuidMap.values());

    if (selectedLocations.length > 0) {
      const locationSet = new Set(selectedLocations.map((l) => l.uuid));
      filteredUuids = filteredUuids.filter((u) => locationSet.has(u.location?.uuid));
    }

    if (selectedLevels.length > 0) {
      const levelSet = new Set(selectedLevels.map((l) => l.uuid));
      filteredUuids = filteredUuids.filter((u) => levelSet.has(u.level?.uuid));
    }

    const companiesMap = new Map();
    filteredUuids.forEach((u) => {
      if (u.engagement?.company?.uuid && !companiesMap.has(u.engagement?.company?.uuid)) {
        companiesMap.set(u.engagement?.company?.uuid, u.engagement?.company);
      }
    });

    return Array.from(companiesMap.values());
  }
);

export const locationsFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector],
  (alternatives) => {
    const locations = alternatives.reduce((acc, alternative) => {
      return [...acc, ...alternative.uuidList.map((uuid) => uuid.location)];
    }, []);

    return locations.filter(
      (location, index, self) => index === self.findIndex((l) => l?.uuid === location?.uuid)
    );
  }
);

export const availableLocationsFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector, filtersSelector],
  (alternatives, filters) => {
    const { companies: selectedCompanies, levels: selectedLevels } = filters;

    const uuidMap = new Map();
    alternatives.reduce((_, alternative) => {
      alternative.originalUuidList.forEach((u) => {
        if (u?.uuid && !uuidMap.has(u.uuid)) {
          uuidMap.set(u.uuid, u);
        }
      });
    }, null);

    let filteredUuids = Array.from(uuidMap.values());

    if (selectedCompanies.length > 0) {
      const companySet = new Set(selectedCompanies.map((c) => c.uuid));
      filteredUuids = filteredUuids.filter((u) => companySet.has(u.engagement?.company?.uuid));
    }

    if (selectedLevels.length > 0) {
      const levelSet = new Set(selectedLevels.map((l) => l.uuid));
      filteredUuids = filteredUuids.filter((u) => levelSet.has(u.level?.uuid));
    }

    const locationsMap = new Map();
    filteredUuids.forEach((u) => {
      if (u.location?.uuid && !locationsMap.has(u.location.uuid)) {
        locationsMap.set(u.location.uuid, u.location);
      }
    });

    return Array.from(locationsMap.values());
  }
);

export const levelsFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector],
  (alternatives) => {
    const levels = alternatives.reduce((acc, alternative) => {
      return [...acc, ...alternative.uuidList.map((uuid) => uuid.level)];
    }, []);

    return levels.filter(
      (level, index, self) => index === self.findIndex((l) => l?.uuid === level?.uuid)
    );
  }
);

export const availableLevelsFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector, filtersSelector],
  (alternatives, filters) => {
    const { companies: selectedCompanies, locations: selectedLocations } = filters;

    const uuidMap = new Map();
    alternatives.reduce((_, alternative) => {
      alternative.originalUuidList.forEach((u) => {
        if (u?.uuid && !uuidMap.has(u.uuid)) {
          uuidMap.set(u.uuid, u);
        }
      });
    }, null);

    let filteredUuids = Array.from(uuidMap.values());

    if (selectedCompanies.length > 0) {
      const companySet = new Set(selectedCompanies.map((c) => c.uuid));
      filteredUuids = filteredUuids.filter((u) => companySet.has(u.engagement?.company?.uuid));
    }

    if (selectedLocations.length > 0) {
      const locationSet = new Set(selectedLocations.map((l) => l.uuid));
      filteredUuids = filteredUuids.filter((u) => locationSet.has(u.location?.uuid));
    }

    const levelMap = new Map();
    filteredUuids.forEach((u) => {
      if (u.level?.uuid && !levelMap.has(u.level.uuid)) {
        levelMap.set(u.level.uuid, u.level);
      }
    });

    return Array.from(levelMap.values());
  }
);

export const engagementTypesFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector],
  (alternatives) => {
    const engagementTypes = alternatives.reduce((acc, alternative) => {
      return [...acc, ...alternative.uuidList.map((uuid) => uuid.engagement.type)];
    }, []);

    return engagementTypes.filter(
      (engagementType, index, self) =>
        index === self.findIndex((e) => e.uuid === engagementType.uuid)
    );
  }
);

export default slice.reducer;
